import { Component, OnInit, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EventService } from 'src/app/services/event.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GroupEvent } from 'src/app/objects/group-event';
import { GGKResponse } from 'src/app/objects/ggkResponse';
import { Router } from '@angular/router';
import { GroupService } from 'src/app/services/group.service';
import { Group } from 'src/app/objects/group';

export interface CreateEventData {
  group_id?: number;
  name?: string;
  event_date?: string;
  event_type?: string;
  description?: string;
  is_private?: string;
}

@Component({
  selector: 'app-create-event-dialog',
  templateUrl: './create-event-dialog.component.html',
  styleUrls: ['./create-event-dialog.component.scss']
})
export class CreateEventDialogComponent implements OnInit {
  model: CreateEventData = {
    name: '',
    event_date: '',
    event_type: '',
    description: '',
    is_private: '0'
  };

  saving = false;
  eventTypes: any[] = [];
  loadingEventTypes = false;
  adminGroups: Group[] = [];
  isPrivateChecked = false;

  constructor(
    public dialogRef: MatDialogRef<CreateEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private eventService: EventService,
    private snackBar: MatSnackBar,
    private router: Router,
    private groupService: GroupService
  ) {
    if (data && data.group_id) {
      this.model.group_id = data.group_id;
    }
  }

  ngOnInit(): void {
    // Load admin groups
    this.adminGroups = this.groupService.getAdminGroups() || [];
    
    // Load event types for master events (is_master = 1)
    this.loadingEventTypes = true;
    this.eventService.getEventTypes(1).subscribe(
      resp => {
        try {
          const parsed = resp.d;
          this.eventTypes = JSON.parse(parsed.data);
        } catch (e) {
          console.error('Failed to parse getEventTypes response', e, resp);
          this.eventTypes = [];
        }
        this.loadingEventTypes = false;
      },
      err => {
        console.error('Failed to load event types', err);
        this.loadingEventTypes = false;
      }
    );
  }

  canSubmit(): boolean {
    return !!(this.model.group_id && this.model.name && this.model.event_date && this.model.event_type);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (!this.canSubmit() || this.saving) {
      return;
    }

    // Parse date and time
    let dateText = this.model.event_date || '';
    let timeText = '00:00:00';
    if (dateText && dateText.indexOf('T') >= 0) {
      const parts = dateText.split('T');
      dateText = parts[0];
      if (parts[1]) {
        timeText = parts[1] + ':00';
      }
    }

    const isPrivate = this.isPrivateChecked ? '1' : '0';
    const groupId = this.model.group_id ? this.model.group_id.toString() : '';

    this.saving = true;

    // Create GroupEvent object with all required properties
    const groupEvent = new GroupEvent();
    groupEvent.group_event_id = '0';
    groupEvent.name = this.model.name || '';
    groupEvent.event_date = dateText;
    groupEvent.event_type = this.model.event_type ? this.model.event_type.toString() : '0';
    groupEvent.event_scoring = '1';
    groupEvent.status = '0';
    groupEvent.course_id = '0';
    groupEvent.tees_id = '0';
    groupEvent.registration_start = '';
    groupEvent.registration_end = '';
    groupEvent.group_id = groupId;
    groupEvent.is_multi_round = '1';
    groupEvent.multi_round_id = '0';
    groupEvent.is_browsable = '1';
    groupEvent.sponsor_count = '0';
    groupEvent.player_count = '0';
    groupEvent.is_private = isPrivate;
    groupEvent.points_system = '0';
    groupEvent.nbr_of_holes = '18';
    groupEvent.recurrence_id = '0';
    groupEvent.is_ryder_cup = '0';
    groupEvent.hide_leaderboard = '0';
    groupEvent.event_image_url = '';
    groupEvent.use_hcp = '0';
    groupEvent.percent_hcp = '0';
    groupEvent.description = this.model.description || '';
    groupEvent.group_eventcol = '';

    // Call the new ggkCreateMasterEvent method with GroupEvent object
    this.eventService.ggkCreateMasterEvent(groupEvent).subscribe(
      (resp: any) => {
        this.saving = false;
        
        try {
          console.log('Raw response:', resp);
          
          // Handle both string and object responses
          let parsed = typeof resp.d === 'string' ? JSON.parse(resp.d) : resp.d;
          console.log('Parsed response:', parsed);
          
          // Check if response is nested under .response property
          const ggkResponse: GGKResponse = parsed.response || parsed;
          console.log('GGKResponse:', ggkResponse);
          
          if (ggkResponse.status === 1) {
            // Success - navigate to the event
            try {
              const responseData = JSON.parse(ggkResponse.data);
              const eventId = responseData.group_event_id || responseData.groupEventId;
              const groupId = this.model.group_id;
              
              if (eventId && groupId) {
                this.snackBar.open('Event created successfully', 'Close', { duration: 3000 });
                this.dialogRef.close(true);
                this.router.navigate(["event-builder",groupId, eventId, 0]);
              } else {
                console.error('Missing event ID or group ID in response', responseData);
                this.snackBar.open('Event created but unable to navigate', 'Close', { duration: 3000 });
                this.dialogRef.close(true);
              }
            } catch (parseError) {
              console.error('Failed to parse response data', parseError, ggkResponse.data);
              this.snackBar.open('Event created successfully', 'Close', { duration: 3000 });
              this.dialogRef.close(true);
            }
          } else {
            // Failed - status === 0
            const errorMessage = ggkResponse.data || 'Failed to create event';
            console.error('Create event failed:', errorMessage);
            this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
          }
        } catch (e) {
          console.error('Failed to parse response', e, resp);
          this.snackBar.open('Unexpected error creating event', 'Close', { duration: 5000 });
        }
      },
      err => {
        this.saving = false;
        console.error('Failed to create event', err);
        this.snackBar.open('Failed to create event', 'Close', { duration: 5000 });
      }
    );
  }
}
